import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';
import { createRegion, deleteRegion, fetchRegions, updateRegion } from './regionsApi';
import type { Region } from './regionsApi';
import { usageText } from './regionsCalc';

export type RegionAction = 'save' | 'delete';

/** Данные экрана «Список регионов»: загрузка, добавление, переименование (с переносом привязок) и удаление */
export function useRegions() {
  const [regions, setRegions] = useState<Region[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<RegionAction | null>(null);

  const requestRef = useRef(0);
  const pendingRef = useRef(false);

  /** keepRows: после действия на экране остаются прежние строки (приглушенные), а не пустой экран */
  const load = useCallback(async (keepRows: boolean) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    if (!keepRows) setRegions(null);

    try {
      const list = await fetchRegions();
      // Устаревший ответ (экран закрыт или список уже перечитан) отбрасывается
      if (requestId !== requestRef.current) return;
      setRegions(list);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      const reason = apiErrorMessage(err, 'Проверьте соединение и повторите попытку');
      if (keepRows) notify(`Не удалось обновить список регионов. ${reason}`, 'error');
      else setError(reason);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  /** Выполняет изменение и перечитывает список: после сбоя состояние на сервере могло измениться */
  const run = useCallback(
    async (action: RegionAction, task: () => Promise<string>, failText: string): Promise<boolean> => {
      if (pendingRef.current) return false;
      pendingRef.current = true;
      setPending(action);

      let done = false;
      try {
        notify(await task(), 'success');
        done = true;
      } catch (err) {
        notify(apiErrorMessage(err, failText), 'error');
      } finally {
        pendingRef.current = false;
        setPending(null);
      }

      load(true);
      return done;
    },
    [load]
  );

  /** target: редактируемый регион или null для нового */
  const saveRegion = useCallback(
    (target: Region | null, name: string) => {
      if (!target) {
        return run('save', async () => {
          await createRegion(name);
          return `Регион «${name}» добавлен`;
        }, 'Не удалось добавить регион');
      }
      return run('save', async () => {
        const moved = await updateRegion(target.id, name);
        const carried = usageText({ usersCount: moved.users, terminalsCount: moved.terminals });
        return carried ? `Регион «${name}» обновлен. Перенесено: ${carried}` : `Регион «${name}» обновлен`;
      }, 'Не удалось сохранить регион');
    },
    [run]
  );

  const removeRegion = useCallback(
    (region: Region) =>
      run('delete', async () => {
        await deleteRegion(region.id);
        return `Регион «${region.name}» удален`;
      }, 'Не удалось удалить регион'),
    [run]
  );

  return { regions, loading, error, retry, pending, saveRegion, removeRegion };
}
